import { createContext, useContext, useState, useEffect } from 'react';
import { useCurrentUser, useAuthToken, useAuthState, useSignOut } from '@mystudioclass/auth';

const API_URL = import.meta.env.VITE_API_URL || 'https://studioscale-backend-production.up.railway.app';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const { user: betterAuthUser, isLoaded: betterAuthLoaded } = useCurrentUser();
  const { signOut } = useSignOut();
  const { getToken, isLoaded: tokenLoaded } = useAuthToken();
  const { isSignedIn } = useAuthState();

  const [user, setUser] = useState(null);
  const [studio, setStudio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoaded, setProfileLoaded] = useState(false);

  // Fetch user profile from our backend when Better Auth is signed in
  useEffect(() => {
    const fetchProfile = async () => {
      if (!betterAuthLoaded) return;

      if (!isSignedIn) {
        setUser(null);
        setStudio(null);
        setLoading(false);
        setProfileLoaded(false);
        return;
      }

      try {
        const token = await getToken();
        if (!token) {
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_URL}/profile/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();

        if (data.success && data.data.exists) {
          setUser(data.data.user);
          setStudio(data.data.studio);
          setProfileLoaded(true);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [betterAuthLoaded, isSignedIn, getToken]);

  // Listen for settings updates from WebSocket
  useEffect(() => {
    const handleSettingsUpdate = (event) => {
      const { settings } = event.detail;
      setStudio(prev => prev ? { ...prev, settings } : null);
    };

    window.addEventListener('settings:updated', handleSettingsUpdate);
    return () => window.removeEventListener('settings:updated', handleSettingsUpdate);
  }, []);

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
      setStudio(null);
      setProfileLoaded(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isAuthenticated = isSignedIn && profileLoaded;

  const value = {
    user,
    studio,
    loading: loading || !betterAuthLoaded,
    isLoaded: betterAuthLoaded && tokenLoaded,
    isAuthenticated,
    isSignedIn,
    getToken,
    logout,
    setUser,
    setStudio,
    // For backwards compatibility with components that might use these
    login: () => console.warn('Use Better Auth sign-in instead'),
    register: () => console.warn('Use Better Auth sign-up instead'),
    setIsAuthenticated: () => {},
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
